/**
 * BADU — scroll reveal
 *
 * Any element marked `data-badu-reveal` fades and rises into place the first
 * time it enters the viewport. Direct children stagger automatically, so a
 * section only has to mark its content wrapper.
 *
 * Shared across every BADU section so the whole store animates on one curve.
 * All motion is CSS; this file only decides *when*. Under
 * `prefers-reduced-motion` everything is revealed immediately.
 */
(function () {
  const SELECTOR = '[data-badu-reveal]';
  const VISIBLE = 'is-revealed';

  /**
   * Horizon's own sections can't carry `data-badu-reveal` without editing core
   * theme files, so they opt in by selector here instead. Each entry must be a
   * wrapper whose *direct children* are the things that should stagger — a
   * product grid's cards, a section header's title and link.
   */
  const ADOPT = ['.section-resource-list__header', '.resource-list'];

  /**
   * Tells the inline bootstrap in snippets/scripts.liquid that this file
   * arrived. That bootstrap sets the `.badu-motion` class which gates the
   * hiding CSS, and on window load removes it again if this flag is missing —
   * so a blocked or failed script leaves every section plainly visible rather
   * than permanently blank.
   */
  window.__baduReveal = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Assigns each child its position in the stagger. */
  function index(group) {
    const children = Array.from(group.children);
    children.forEach((child, i) => {
      child.style.setProperty('--badu-stagger-index', String(i));
    });
    // A group with no children staggers as a single unit.
    if (children.length === 0) {
      group.style.setProperty('--badu-stagger-index', '0');
    }
  }

  function revealNow(group) {
    group.classList.add(VISIBLE);
  }

  let observer = null;

  function getObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          revealNow(entry.target);
          // One-shot: never re-hide on scroll back up.
          observer.unobserve(entry.target);
        }
      },
      {
        // Fire slightly before the element is fully on screen so the motion
        // finishes about when the reader arrives at it.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.01,
      }
    );

    return observer;
  }

  /** Tags Horizon's native wrappers so they flow through the same path. */
  function adopt(scope) {
    for (const selector of ADOPT) {
      for (const el of scope.querySelectorAll(selector)) {
        el.setAttribute('data-badu-reveal', '');
      }
    }
  }

  function register(root) {
    const scope = root instanceof Element ? root : document;
    adopt(scope);
    const groups = scope.querySelectorAll(SELECTOR);

    for (const group of groups) {
      if (group.dataset.baduRevealBound === 'true') continue;
      group.dataset.baduRevealBound = 'true';

      index(group);

      if (reducedMotion.matches) {
        revealNow(group);
        continue;
      }

      getObserver().observe(group);
    }
  }

  function init() {
    register(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The theme editor swaps section markup in place; re-scan what it inserted.
  document.addEventListener('shopify:section:load', (event) => {
    register(event.target);
  });

  // If the reader flips the OS setting mid-session, stop animating.
  reducedMotion.addEventListener('change', () => {
    if (!reducedMotion.matches) return;
    document.querySelectorAll(SELECTOR).forEach(revealNow);
  });
})();
